/*
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

import {
  EnvironmentInjector,
  ErrorHandler,
  inject,
  Injectable,
  runInInjectionContext,
} from '@angular/core';
import {
  NbAuthIllegalTokenError,
  NbAuthResult,
  NbAuthStrategy,
} from '@nebular/auth';
import { from, Observable, of as observableOf } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Auth, idToken, UserCredential } from '@angular/fire/auth';

@Injectable()
export abstract class NbFirebaseBaseStrategy extends NbAuthStrategy {
  protected afAuth = inject(Auth);
  protected errorHandler = inject(ErrorHandler);
  private injector = inject(EnvironmentInjector);

  /**
   * Runs a call to a zone-wrapped AngularFire API (`signInWithPopup`,
   * `idToken`, ...) inside an injection context. These methods are invoked
   * from NbAuthService, not from a constructor, so without this AngularFire
   * falls back to the raw Firebase function and results can land outside
   * Angular's zone.
   */
  protected inContext<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  logout(): Observable<NbAuthResult> {
    const module = 'logout';
    return from(this.afAuth.signOut()).pipe(
      map(() => {
        return new NbAuthResult(
          true,
          null,
          this.getOption(`${module}.redirect.success`),
          [],
          this.getOption(`${module}.defaultMessages`),
        );
      }),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  register(data?: any): Observable<NbAuthResult> {
    throw new Error(
      `'register' is not supported by '${this.constructor.name}', use 'authenticate'.`,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  requestPassword(data?: any): Observable<NbAuthResult> {
    throw new Error(
      `'requestPassword' is not supported by '${this.constructor.name}', use 'authenticate'.`,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  resetPassword(data: any = {}): Observable<NbAuthResult> {
    throw new Error(
      `'resetPassword' is not supported by '${this.constructor.name}', use 'authenticate'.`,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  refreshToken(data: any = {}): Observable<NbAuthResult> {
    throw new Error(
      `'refreshToken' is not supported by '${this.constructor.name}', use 'authenticate'.`,
    );
  }

  protected processFailure(
    error: any,
    module: string,
  ): Observable<NbAuthResult> {
    const errorMessages = [];

    this.errorHandler.handleError(error);

    if (error instanceof NbAuthIllegalTokenError) {
      errorMessages.push(error.message);
    } else {
      errorMessages.push(
        this.getOption('errors.getter')(module, error, this.options),
      );
    }

    return observableOf(
      new NbAuthResult(
        false,
        error,
        this.getOption(`${module}.redirect.failure`),
        errorMessages,
        [],
      ),
    );
  }

  protected processSuccess(
    res: UserCredential | null,
    module: string,
  ): Observable<NbAuthResult> {
    return this.inContext(() => idToken(this.afAuth)).pipe(
      map((token) => {
        return new NbAuthResult(
          true,
          res,
          this.getOption(`${module}.redirect.success`),
          [],
          this.getOption('messages.getter')(module, res, this.options),
          this.createToken(token),
        );
      }),
    );
  }
}
